import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ListResponse } from "@app/api/client";
import { api } from "@app/api/client";
import type { BackofficeUser, BackofficeUserDetail } from "@app/api/types";

export interface UserListParams {
  offset: number;
  limit: number;
  q?: string;
  enabled?: boolean;
  verified?: boolean;
}

export function useUsers(params: UserListParams) {
  return useQuery({
    queryKey: ["users", params],
    queryFn: async (): Promise<ListResponse<BackofficeUser>> => {
      const query: Record<string, string | number | boolean> = {
        offset: params.offset,
        limit: params.limit
      };
      if (params.q) query.q = params.q;
      if (params.enabled !== undefined) query.enabled = params.enabled;
      if (params.verified !== undefined) query.verified = params.verified;
      const { data } = await api.get<ListResponse<BackofficeUser>>("/admin/users", { params: query });
      return data;
    }
  });
}

export function useUser(id: number) {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async (): Promise<BackofficeUserDetail> => {
      const { data } = await api.get<BackofficeUserDetail>(`/admin/users/${id}`);
      return data;
    }
  });
}

export function useSetUserEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: number; enabled: boolean }): Promise<BackofficeUser> => {
      const { data } = await api.post<BackofficeUser>(`/admin/users/${id}/${enabled ? "enable" : "disable"}`);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    }
  });
}
